import React, { useContext, useState } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import message from "antd/lib/message";
import Form from "antd/lib/form";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import { CrampingSection } from "./crampingSection";
import { HeadacheSection } from "./headacheSection";
import { DizzynessSection } from "./dizzynessSection";
import { PassingOutSection } from "./passingOutSection";
import { NauseaVomitingSection } from "./nauseaVomitingSection";
import { ChestPainSection } from "./chestPainSection";
import { ShortnessOfBreathSection } from "./shortnessOfBreathSection";
import { LegSwellingSectionSection } from "./legSwellingSection";
import { WeightGainSection } from "./weightGainSection";
import { OtherSection } from "./otherSection";
import { UserContext, UserContextState } from "contextApis/userContext";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { useMutation } from "react-query";
import { createApiClient } from "apiClient";
import { Platform, UserRoles } from "constants/roles";
import { FormSubmitButton } from "pages/symptoms/surveyForms/FormSubmitButton";
import { FormClearButton } from "pages/symptoms/surveyForms/FormClearButton";
import {
  SurveyPage,
  SurveyContent,
  SurveyHeader,
  SurveyProgressBar,
  SurveyFooter,
  FooterActions
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface DialysisSymptomsFormProps {
  setShowSurveyForm(show: boolean): void;
  selectedItems: string[];
  goToNextFormOrReturn(): void;
}

export const DialysisSymptomsForm: React.FC<DialysisSymptomsFormProps> = ({
  setShowSurveyForm,
  selectedItems,
  goToNextFormOrReturn
}) => {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  const [currentPage, setCurrentPage] = useState(1);
  const [otherInput, setOtherInput] = useState("");

  const hideClearButtonPages = [2, 4, 9, 11, 13, 15];

  const [form] = Form.useForm();

  const userContext = useContext<UserContextState>(UserContext);
  const patientContext = useContext<PatientContextState>(PatientContext);

  const userRole = sessionStorage.getItem("userRole");

  const getDialysisValue = (key: string) => {
    const val = localStorage.getItem(key) || sessionStorage.getItem(key);
    return val && val.trim() !== "" ? val : null;
  };

  const {
    isLoading: isLoadingDialysisSymptomMutation,
    mutate: submitDialysisSymptomForm
  } = useMutation<string>(
    "add-dialysis-form",
    async () => {
      return await createApiClient().addDialysisFormData({
        patient_internal_id:
          userRole === UserRoles.PATIENT && userContext.userData
            ? userContext.userData?.internal_id
            : patientContext?.patientData?.id || "",
        dizzy: getDialysisValue("feelingDizzyRecently"),
        passedout: getDialysisValue("haveYouPassedOut"),
        cramping: getDialysisValue("haveYouHadCramping"),
        headaches: getDialysisValue("haveYouHadHeadAches"),
        nausea: getDialysisValue("haveYouHadNauseaOrVomiting"),
        chestpain: getDialysisValue("haveYouHadAnyChestPain"),
        swelling: getDialysisValue("haveYouHadAnyLegSwelling"),
        breath: getDialysisValue("haveYouHadShortnessOfBreath"),
        weight: getDialysisValue("howMuchWeightDoYouGain"),
        other: otherInput === "" ? null : otherInput,
        submitted_by: `${userContext.userData?.firstName} ${userContext.userData?.lastName}`
      });
    },
    {
      onSuccess: () => {
        message.success("Form Submitted Successfully");
        goToNextFormOrReturn();
      },
      onError: () => {
        message.error({
          content: (
            <span>
              There was a problem submitting this form, resubmit or{" "}
              <a onClick={() => goToNextFormOrReturn()}>
                click here to continue to the next survey
              </a>
            </span>
          ),
          style: {
            bottom: "10px",
            position: "fixed",
            left: "30%"
          },
          duration: 5
        });
      }
    }
  );

  const onFinish = () => {
    if (currentPage < 16) {
      setCurrentPage(prev => prev + 1);
    } else {
      submitDialysisSymptomForm();
    }
  };

  const handleGoBack = () => {
    setCurrentPage(prev => (prev > 1 ? prev - 1 : 1));
  };

  const onClear = () => {
    if (currentPage === 1) {
      form.resetFields(["Have you been feeling dizzy"]);
      localStorage.setItem("feelingDizzyRecently", "");
      sessionStorage.setItem("feelingDizzyRecently", "");
    } else if (currentPage === 3) {
      form.resetFields(["Have you passed out"]);
      localStorage.setItem("haveYouPassedOut", "");
      sessionStorage.setItem("haveYouPassedOut", "");
    } else if (currentPage === 5) {
      form.resetFields(["Have you had cramping"]);
      localStorage.setItem("haveYouHadCramping", "");
      sessionStorage.setItem("haveYouHadCramping", "");
    } else if (currentPage === 6) {
      form.resetFields(["Have you had headaches recently"]);
      localStorage.setItem("haveYouHadHeadAches", "");
      sessionStorage.setItem("haveYouHadHeadAches", "");
    } else if (currentPage === 7) {
      form.resetFields(["Have you had nausea or vomiting"]);
      localStorage.setItem("haveYouHadNauseaOrVomiting", "");
      sessionStorage.setItem("haveYouHadNauseaOrVomiting", "");
    } else if (currentPage === 8) {
      form.resetFields(["Have you had any chest pain"]);
      localStorage.setItem("haveYouHadAnyChestPain", "");
      sessionStorage.setItem("haveYouHadAnyChestPain", "");
    } else if (currentPage === 10) {
      form.resetFields(["Have you had any shortness of breath"]);
      localStorage.setItem("haveYouHadShortnessOfBreath", "");
      sessionStorage.setItem("haveYouHadShortnessOfBreath", "");
    } else if (currentPage === 12) {
      form.resetFields(["Have you had any leg swelling"]);
      localStorage.setItem("haveYouHadAnyLegSwelling", "");
      sessionStorage.setItem("haveYouHadAnyLegSwelling", "");
    } else if (currentPage === 14) {
      form.resetFields(["How much weight do you gain"]);
      localStorage.setItem("howMuchWeightDoYouGain", "");
      sessionStorage.setItem("howMuchWeightDoYouGain", "");
    } else if (currentPage === 16) {
      form.resetFields(["Any other symptoms"]);
      setOtherInput("");
    }
  };

  return (
    <SurveyPage platform={platform}>
      <div
        style={{
          marginBottom: "15px",
          display: platform === Platform.MOBILE ? "none" : "block"
        }}
      >
        <AppButton
          type={ButtonType.Primary}
          onClick={() => setShowSurveyForm(false)}
          buttonContent={
            <>
              <ArrowLeftOutlined /> Back
            </>
          }
        />
      </div>

      <SurveyContent>
        <Form
          name="urinarySymptoms"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          style={{ padding: "0px" }}
        >
          <SurveyHeader
            title={`Survey - ${selectedItems[0]}`}
            onBack={
              platform === Platform.MOBILE
                ? undefined
                : () => setShowSurveyForm(false)
            }
          />

          <SurveyProgressBar currentStep={currentPage} totalSteps={16} />

          <DizzynessSection currentPage={currentPage} />
          <PassingOutSection currentPage={currentPage} />
          <CrampingSection currentPage={currentPage} />
          <HeadacheSection currentPage={currentPage} />
          <NauseaVomitingSection currentPage={currentPage} />
          <ChestPainSection currentPage={currentPage} />
          <ShortnessOfBreathSection currentPage={currentPage} />
          <LegSwellingSectionSection currentPage={currentPage} />
          <WeightGainSection currentPage={currentPage} />
          <OtherSection
            currentPage={currentPage}
            setOtherInput={setOtherInput}
            otherInput={otherInput}
          />

          <SurveyFooter>
            <FooterActions
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <Form.Item style={{ marginBottom: 0 }}>
                {!hideClearButtonPages.includes(currentPage) && (
                  <FormClearButton onClear={onClear} />
                )}
              </Form.Item>
              <div style={{ display: "flex", gap: "10px" }}>
                {currentPage !== 1 && (
                  <AppButton
                    type={ButtonType.Primary}
                    buttonContent={"Back"}
                    onClick={() => handleGoBack()}
                    style={{ width: "90px" }}
                  />
                )}
                <FormSubmitButton
                  type={ButtonType.Primary}
                  buttonContent={currentPage === 16 ? "Submit" : "Next"}
                  htmlType="submit"
                  loading={isLoadingDialysisSymptomMutation}
                />
              </div>
            </FooterActions>
          </SurveyFooter>
        </Form>
      </SurveyContent>
    </SurveyPage>
  );
};
